import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { CampoQuantidade } from '../componentes/Campos';
import { TabelaComparativo, type OrigemLinha } from '../componentes/TabelaComparativo';
import { useFornecedores } from '../servicos/fornecedores';
import { useProdutos } from '../servicos/produtos';
import { useItens, useRespostasDoProduto } from '../servicos/cotacoes';

/**
 * Quem cotou este produto, e por quanto sai de verdade.
 *
 * A quantidade e UMA SO para todas as linhas, de proposito. Comparar
 * fornecedores usando a quantidade que cada um respondeu compararia compras
 * diferentes — o numero final nao diria qual sai mais barato, diria qual pediu
 * menos. Aqui a pergunta e: "para o tanto que EU quero comprar, quanto custa em
 * cada um?" (RN-046)
 *
 * E ela e editavel porque a resposta muda com o volume: um fornecedor com
 * minimo alto e frete caro perde no lote pequeno e ganha no grande. Mexer no
 * campo mostra a virada.
 */
export function ComparativoProduto() {
  const { id: cotacaoId, produtoId } = useParams<{ id: string; produtoId: string }>();
  const { respostas, carregando, erro } = useRespostasDoProduto(produtoId ?? null);
  const { fornecedores } = useFornecedores();
  const { produtos } = useProdutos();
  const itens = useItens(cotacaoId ?? null);

  const produto = produtos.find((p) => p.id === produtoId);
  const itemDaCotacao = itens.find((i) => i.produtoId === produtoId);

  const [quantidade, setQuantidade] = useState<number | null>(null);

  // A quantidade da cotação de origem é o ponto de partida — mas só até a
  // pessoa mexer, e por isso a dependência é o item, não o valor atual.
  useEffect(() => {
    if (itemDaCotacao) setQuantidade(itemDaCotacao.quantidadeDesejadaMilli);
  }, [itemDaCotacao?.id]);

  const nomeFornecedor = useMemo(
    () =>
      Object.fromEntries(
        fornecedores.map((f) => [f.id, f.nomeFantasia || f.razaoSocial]),
      ) as Record<string, string>,
    [fornecedores],
  );

  const origens: OrigemLinha[] = useMemo(() => {
    if (!quantidade || quantidade <= 0 || !produtoId) return [];
    return respostas.map((r) => ({
      // O id da resposta e o id do ITEM: quatro fornecedores respondendo o
      // mesmo produto devolvem quatro documentos com o MESMO id. A cotacao de
      // origem e o que separa um do outro.
      id: `${r.cotacaoId}-${r.id}`,
      produtoId,
      produtoNome: produto?.nome ?? 'produto',
      fornecedorId: r.fornecedorId,
      fornecedorNome: nomeFornecedor[r.fornecedorId] ?? 'fornecedor removido',
      unidade: produto?.unidadeMedida ?? null,
      quantidadeDesejadaMilli: quantidade,
      resposta: r,
      caminho: `/cotacoes/${r.cotacaoId}`,
    }));
  }, [respostas, quantidade, produtoId, produto, nomeFornecedor]);

  if (carregando) return <Carregando />;

  return (
    <div className="flex flex-col gap-5">
      <Cabecalho
        titulo={produto?.nome ?? 'Produto'}
        descricao="Todos os fornecedores que cotaram este item, na mesma quantidade."
        acoes={
          <Link
            to={cotacaoId ? `/cotacoes/${cotacaoId}` : '/cotacoes'}
            className="inline-flex items-center gap-1.5 rounded border border-regua-2 px-3 py-1.5 text-sm hover:border-marca"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Voltar à cotação
          </Link>
        }
      />

      {erro && (
        <Erro
          titulo="Não foi possível ler as respostas dos outros fornecedores"
          detalhe={erro}
        />
      )}

      <div className="flex flex-wrap items-end gap-3 rounded-md border border-regua-2 bg-superficie p-3">
        <div className="w-48">
          <CampoQuantidade
            rotulo="Quantidade a comprar"
            milli={quantidade}
            aoMudar={setQuantidade}
            dica="A mesma para todos — é o que torna a comparação honesta."
          />
        </div>
        <p className="flex-1 text-xs text-tinta-3">
          Mude a quantidade e a ordem pode virar: fornecedor com quantidade mínima alta e frete caro
          perde no lote pequeno e ganha no grande.
        </p>
      </div>

      {respostas.length === 0 ? (
        <Vazio
          icone={<Users className="size-8" />}
          titulo="Nenhum fornecedor respondeu por este produto ainda"
          descricao="Assim que houver resposta em qualquer cotação para este item, ela aparece aqui — venha de que cotação vier."
        />
      ) : origens.length === 0 ? (
        <p className="text-sm text-tinta-3">
          Informe a quantidade que você pretende comprar para o comparativo ser calculado.
        </p>
      ) : (
        <>
          <TabelaComparativo origens={origens} />
          <p className="text-xs text-tinta-3">
            <span className="num">{respostas.length}</span>{' '}
            {respostas.length === 1 ? 'resposta' : 'respostas'} de{' '}
            <span className="num">{new Set(respostas.map((r) => r.fornecedorId)).size}</span>{' '}
            {new Set(respostas.map((r) => r.fornecedorId)).size === 1 ? 'fornecedor' : 'fornecedores'}.
          </p>
        </>
      )}
    </div>
  );
}
